import { UpdatePersonalDetailsFormData } from '@pages/Profile/UpdatePersonalDetails/UpdatePersonalDetails.types';
import {
  alphabetNumberSpaceRegex,
  alphabetWithSeperatorType,
  nameRegex,
  alphabetNumberRegex,
  passwordRegex,
  phoneRegex,
  reqLowerRegex,
  reqNumbersRegex,
  reqUpperRegex,
  specialCharsRegex,
} from '@utils/validation/consts';
import * as yup from 'yup';
import moment from 'moment';
import { UpdateBankDetailsFormData } from '@pages/Profile/UpdateBankDetails/UpdateBankDetails.types';
import { UpdateTaxDetailsFormData } from '@pages/Profile/UpdateTaxDetails/UpdateTaxDetails.types';
import { PasswordFormData } from '@pages/Profile/UpdatePassword/UpdatePassword.types';

yup.setLocale({
  mixed: {
    required: ({ path }) => `investment.errors.${path}.required`,
  },
  string: {
    matches: ({ path }) => `investment.errors.${path}.matches`,
  },
});

export function nullableTransformFn(
  this: yup.StringSchema<string | null | undefined>
) {
  return this.nullable().transform((value) =>
    value && value !== '' ? value : null
  );
}

yup.addMethod<yup.StringSchema<string | null | undefined>>(
  yup.string,
  'nullableTransform',
  nullableTransformFn
);

export const updatePersonalDetailsFormSchema = yup
  .object<Record<keyof UpdatePersonalDetailsFormData, yup.AnySchema>>()
  .shape(
    {
      firstName: yup.string().nullableTransform().trim(),
      middleName: yup.string().nullableTransform(),
      lastName: yup.string().nullableTransform().trim(),
      email: yup
        .string()
        .nullableTransform()
        .email('signUp.errors.emailAddress.format'),
      address: yup.string().nullableTransform().trim(),
      city: yup.string().nullableTransform().trim(),
      state: yup.string().nullableTransform().trim(),
      zipCode: yup.string().nullableTransform().trim(),
      contactDetailsCountry: yup.string().nullableTransform().trim(),
      phoneCountryCode: yup.string().when('phoneNumber', {
        is: (val: string) => val && val !== '',
        then: yup.string().trim().required(),
        otherwise: yup.string().nullableTransform().trim(),
      }),
      phoneNumber: yup.string().when('phoneCountryCode', {
        is: (val: string) => val && val !== '',
        then: yup
          .string()
          .nullableTransform()
          .trim()
          .required()
          .matches(phoneRegex),
        otherwise: yup.string().nullableTransform().trim().matches(phoneRegex),
      }),
      birthDate: yup
        .date()
        .typeError('signUp.errors.birthDate.invalidDate')
        .nullable()
        .test(
          'birthDate min age',
          'signUp.errors.birthDate.minAge',
          (value) => {
            return moment().diff(moment(value), 'years') >= 18;
          }
        ),
    },
    [
      ['phoneNumber', 'phoneCountryCode'],
      ['phoneCountryCode', 'phoneNumber'],
    ]
  )
  .required();

export const updateBankDetailsFormSchema = yup
  .object<Record<keyof UpdateBankDetailsFormData, yup.AnySchema>>()
  .shape(
    {
      bankAccountHolderName: yup
        .string()
        .nullableTransform()
        .trim()
        .matches(nameRegex),
      bankName: yup.string().nullableTransform().trim().matches(nameRegex),
      bankLocation: yup
        .string()
        .nullableTransform()
        .trim()
        .matches(alphabetWithSeperatorType),
      isNominee: yup.boolean(),
      bankAccountNumber: yup.string().when('iban', {
        is: (iban: boolean) => !iban,
        then: yup.string().trim().required().matches(alphabetNumberSpaceRegex),
        otherwise: yup.string().notRequired(),
      }),
      swiftOrBic: yup.string().when('iban', {
        is: (iban: boolean) => !iban,
        then: yup
          .string()
          .trim()
          .required()
          .uppercase()
          .matches(alphabetNumberRegex),
        otherwise: yup.string().notRequired(),
      }),
      iban: yup.string().when('bankAccountNumber', {
        is: (bankAccountNumber: boolean) => !bankAccountNumber,
        then: yup
          .string()
          .trim()
          .required()
          .uppercase()
          .matches(alphabetNumberSpaceRegex),
        otherwise: yup.string().notRequired(),
      }),
    },
    [
      ['bankAccountNumber', 'iban'],
      ['iban', 'bankAccountNumber'],
    ]
  )
  .required();

export const updateTaxDetailsFormSchema = yup
  .object<Record<keyof UpdateTaxDetailsFormData, yup.AnySchema>>()
  .shape(
    {
      primaryTaxResidenceCountry: yup.string().when('primaryTin', {
        is: (val: string) => val && val !== '',
        then: yup.string().required(),
        otherwise: yup.string().nullableTransform().trim(),
      }),
      primaryTin: yup.string().when('noPrimaryIdentificationNumber', {
        is: (val: boolean) => val === false,
        then: yup.string().when('primaryTaxResidenceCountry', {
          is: (val: string) => val && val !== '',
          then: yup
            .string()
            .nullableTransform()
            .trim()
            .required(
              'investment.errors.taxpayerIdentificationNumber.required'
            ),
          otherwise: yup.string().nullableTransform().trim(),
        }),
        otherwise: yup.string().notRequired(),
      }),
      secondaryTaxResidenceCountry: yup.string().when('secondaryTin', {
        is: (val: string) => val && val !== '',
        then: yup.string().trim().required(),
        otherwise: yup.string().notRequired(),
      }),
      secondaryTin: yup.string().when('noSecondaryIdentificationNumber', {
        is: (val: boolean) => val === false,
        then: yup.string().when('secondaryTaxResidenceCountry', {
          is: (val: string) => val && val !== '',
          then: yup
            .string()
            .nullableTransform()
            .required('investment.errors.taxpayerIdentificationNumber.required')
            .trim(),
          otherwise: yup.string().nullableTransform().trim(),
        }),
        otherwise: yup.string().notRequired(),
      }),
      usTaxPerson: yup.boolean(),
      noPrimaryIdentificationNumber: yup.boolean(),
      primaryTinExcuse: yup.string().when('noPrimaryIdentificationNumber', {
        is: (val: boolean) => val,
        then: yup.string().trim().required(),
        otherwise: yup.string().notRequired(),
      }),
      noSecondaryIdentificationNumber: yup.boolean(),
      secondaryTinExcuse: yup.string().when('noSecondaryIdentificationNumber', {
        is: (val: boolean) => val,
        then: yup.string().trim().required(),
        otherwise: yup.string().notRequired(),
      }),
    },
    [
      ['primaryTaxResidenceCountry', 'primaryTin'],
      ['secondaryTaxResidenceCountry', 'secondaryTin'],
      ['primaryTin', 'primaryTaxResidenceCountry'],
      ['secondaryTin', 'secondaryTaxResidenceCountry'],
    ]
  )
  .required();

export const updatePasswordFormSchema = yup
  .object<Record<keyof PasswordFormData, yup.AnySchema>>({
    oldPassword: yup.string().required('profile.errors.oldPassword.required'),
    password: yup
      .string()
      .required('profile.errors.newPassword.required')
      .test({
        name: 'password_validation',
        test: (value, { createError }) => {
          if (!value) return false;

          const errors = [];
          if (!reqLowerRegex.test(value)) errors.push('lowercase');
          if (!reqUpperRegex.test(value)) errors.push('uppercase');
          if (!reqNumbersRegex.test(value)) errors.push('digits');
          if (!specialCharsRegex.test(value)) errors.push('specialChars');
          if (value.length < 8) errors.push('length');

          return errors.length
            ? createError({
                message: errors.join('.'),
                path: 'password',
              })
            : true;
        },
      })
      .matches(passwordRegex, 'profile.errors.newPassword.invalidCharacters'),
    confirmPassword: yup
      .string()
      .when('password', {
        is: (val: string) => (val && val.length > 0 ? true : false),
        then: yup
          .string()
          .oneOf(
            [yup.ref('password')],
            'profile.errors.confirmPassword.different'
          ),
      })
      .required('profile.errors.confirmPassword.required'),
  })
  .required();
